// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {Form} from '@supermove/hooks';
import {ClientModel, PayengineCreditCardModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import Badge from '@shared/design/components/Badge';
import ErrorCallout from '@shared/design/components/Callout/ErrorCallout';
import RadioButton from '@shared/design/components/RadioButton';
import Text from '@shared/design/components/Text';
import DocumentV2ClientCreditCardFields from '@shared/modules/Document/components/DocumentV2ClientCreditCardFields';
import {UseSavePayengineCreditCardForm} from '@shared/modules/Payment/hooks/useSavePayEngineCreditCardMutation';

const Container = Styled.View`
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const CreditCardsListContainer = Styled.View`
  border-radius: 4px;
`;

const CreditCardsListHeader = Styled.View`
  flex-direction: row;
  justify-content: space-between;
`;

const CreditCardsListItemContainer = Styled.View<{isSelected: boolean}>`
  padding-horizontal: 8px;
  padding-vertical: 16px;
  border-radius: 4px;
  ${({isSelected}) => isSelected && `background-color: ${colors.blue.accent};`}
`;

const CreditCardsListItem = ({
  creditCard,
  isSelected,
  onSelect,
}: {
  creditCard: PayengineCreditCardModel;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  return (
    <CreditCardsListItemContainer isSelected={isSelected}>
      <CreditCardsListHeader>
        <Row>
          <RadioButton isOn={isSelected} onPress={onSelect} />
          <Space width={8} />
          <Text.Body>{creditCard.cardSummaryName}</Text.Body>
          {creditCard.isDefault && (
            <React.Fragment>
              <Space width={4} />
              <Badge isResponsive label={'Default'} color={colors.gray.secondary} />
            </React.Fragment>
          )}
        </Row>
        <Text.Body>{`Expires ${creditCard.expireDate}`}</Text.Body>
      </CreditCardsListHeader>
    </CreditCardsListItemContainer>
  );
};

const CreditCardsListItemAdd = ({
  isSelected,
  onSelect,
  client,
  form,
  field,
  setCreditCardClient,
}: {
  isSelected: boolean;
  onSelect: () => void;
  client: ClientModel;
  form: Form<UseSavePayengineCreditCardForm>;
  field: string;
  setCreditCardClient: (client: ClientModel) => void;
}) => {
  return (
    <CreditCardsListItemContainer isSelected={isSelected}>
      <CreditCardsListHeader>
        <Row>
          <RadioButton isOn={isSelected} onPress={onSelect} />
          <Space width={8} />
          <Text.Body>Add New Card</Text.Body>
        </Row>
      </CreditCardsListHeader>
      {isSelected && (
        <Container style={{paddingLeft: 24}}>
          <Space height={16} />
          <DocumentV2ClientCreditCardFields
            client={client}
            form={form}
            field={field}
            setCreditCardClient={setCreditCardClient}
          />
        </Container>
      )}
    </CreditCardsListItemContainer>
  );
};

const DocumentV2ClientCreditCardList = ({
  client,
  form,
  field,
  selectedCreditCardId,
  allowSelectExistingPaymentMethods,
  errorTransactionMessage,
  errorTokenizeMessage,
  setCreditCardClient,
  onSelect,
}: {
  client: ClientModel;
  form: Form<UseSavePayengineCreditCardForm>;
  field: string;
  selectedCreditCardId: string | 'new' | undefined;
  allowSelectExistingPaymentMethods: boolean;
  errorTransactionMessage: string | null;
  errorTokenizeMessage: string | null;
  setCreditCardClient: (client: ClientModel) => void;
  onSelect: (creditCard: PayengineCreditCardModel | undefined) => void;
}) => {
  return (
    <React.Fragment>
      <CreditCardsListContainer>
        {_.map(allowSelectExistingPaymentMethods ? client.creditCards : [], (creditCard, index) => {
          const isSelected = selectedCreditCardId === creditCard.id;
          return (
            <React.Fragment>
              {errorTransactionMessage && isSelected && (
                <React.Fragment>
                  <ErrorCallout text={errorTransactionMessage} />
                  <Space height={4} />
                </React.Fragment>
              )}
              <CreditCardsListItem
                key={creditCard.id}
                creditCard={creditCard}
                isSelected={isSelected}
                onSelect={() => onSelect(creditCard)}
              />
            </React.Fragment>
          );
        })}
        <Container>
          {selectedCreditCardId === 'new' &&
            (!!errorTransactionMessage || !!errorTokenizeMessage) && (
              <React.Fragment>
                <ErrorCallout text={errorTransactionMessage || errorTokenizeMessage} />
                <Space height={4} />
              </React.Fragment>
            )}
          <CreditCardsListItemAdd
            isSelected={selectedCreditCardId === 'new'}
            onSelect={() => onSelect(undefined)}
            client={client}
            form={form}
            field={field}
            setCreditCardClient={setCreditCardClient}
          />
        </Container>
      </CreditCardsListContainer>
    </React.Fragment>
  );
};

DocumentV2ClientCreditCardList.fragment = gql`
  ${DocumentV2ClientCreditCardFields.fragment}

  fragment DocumentV2ClientCreditCardList on Client {
    id
    creditCards {
      id
      cardName
      cardSummaryName
      isDefault
      expireDate
    }
    ...DocumentV2ClientCreditCardFields
  }
`;

export default DocumentV2ClientCreditCardList;
